import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AppLayout from '../layout/AppLayout';
import chatService, { UserSession } from '../services/ChatService';
import MobileHeader from '../layout/MobileHeader';
import { useTranslation } from 'react-i18next';

const SessionsPage: React.FC = () => {

  const [sessions, setSessions] = useState<UserSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const { t } = useTranslation();
  

  useEffect(() => {
    fetchSessions();
  }, []);

  const fetchSessions = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await chatService.getUserSessions();
      setSessions(response.sessions);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch sessions';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const handleSessionClick = (sessionId: string) => {
    // Navigate to chat page - the order will be selected through the orders panel
    // Note: Sessions are now tied to orders. Consider fetching order by session_id in the future.
    navigate('/chat');
  };

  const handleNewChat = () => {
    // Navigate to chat page with a new session
    navigate('/chat');
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="flex-1 overflow-auto bg-main-bg min-h-full">
          <div className="max-w-6xl mx-auto px-3 sm:px-4 md:px-6 py-4 sm:py-8">
            <div className="flex items-center justify-center min-h-[60vh]">
              <div className="text-center">
                <div className="animate-spin rounded-full h-10 w-10 sm:h-12 sm:w-12 border-b-2 border-blue-600 mx-auto"></div>
                <p className="mt-4 text-sm sm:text-base text-gray-600">Loading your sessions...</p>
              </div>
            </div>
          </div>
        </div>
      </AppLayout>
    );
  }

  if (error) {
    return (
      <AppLayout>
        <div className="flex-1 overflow-auto bg-main-bg min-h-full">
          <div className="max-w-6xl mx-auto px-3 sm:px-4 md:px-6 py-4 sm:py-8">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 sm:p-5">
              <div className="flex">
                <div className="ml-3 flex-1">
                  <h3 className="text-sm sm:text-base font-medium text-red-800">
                    Error loading sessions
                  </h3>
                  <div className="mt-2 text-xs sm:text-sm text-red-700">
                    <p>{error}</p>
                  </div>
                  <div className="mt-4">
                    <button
                      onClick={fetchSessions}
                      className="bg-red-100 px-3 sm:px-4 py-1.5 sm:py-2 rounded-md text-red-800 text-xs sm:text-sm hover:bg-red-200 transition-colors"
                    >
                      Try again
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout pageTitle="Chat Sessions">
      <MobileHeader
        title="Chat Sessions"
        showBack={true}
        onBackClick={() => navigate('/chat')}
      />
      <div className="flex-1 overflow-auto bg-main-bg min-h-full">
        <div className="max-w-6xl mx-auto px-3 sm:px-4 md:px-6 py-4 sm:py-8">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 sm:mb-6 gap-3 sm:gap-4">
            <button
              onClick={handleNewChat}
              className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors text-sm sm:text-base font-medium"
            >
              New Chat
            </button>
          </div>

          {sessions.length === 0 ? (
            <div className="text-center py-8 sm:py-12 px-4">
              <div className="text-gray-400 mb-4">
                <svg className="mx-auto h-10 w-10 sm:h-12 sm:w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
              </div>
              <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-2">No chat sessions yet</h3>
              <p className="text-sm sm:text-base text-gray-500 mb-4">Start your first conversation with Cata AI</p>
              <button
                onClick={handleNewChat}
                className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors text-sm sm:text-base font-medium"
              >
                {t('header.buttons.startNewChat', { defaultValue: 'Start New Chat' })}
              </button>
            </div>
          ) : (
            <div className="grid gap-3 sm:gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
              {sessions.map((session) => (
                <div
                  key={session.id}
                  onClick={() => handleSessionClick(session.session_id)}
                  className="bg-white rounded-lg border border-gray-200 p-4 sm:p-5 hover:shadow-md cursor-pointer transition-all active:scale-95"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2 truncate">
                        Session {session.id.slice(-8)}
                      </h3>
                      <div className="flex items-center text-xs sm:text-sm text-gray-400">
                        <svg className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                        </svg>
                        <span className="truncate">Chat Session</span>
                      </div>
                    </div>
                    <div className="ml-3 sm:ml-4 shrink-0">
                      <svg className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="mt-6 sm:mt-8 flex justify-center">
            <button
              onClick={fetchSessions}
              className="text-blue-600 hover:text-blue-700 text-xs sm:text-sm font-medium transition-colors"
            >
              Refresh Sessions
            </button>
          </div>
        </div>
      </div>
    </AppLayout>
  );
};

export default SessionsPage;
